import { useId, useState, type KeyboardEvent } from "react";

interface Props {
  id?: string;
  values: string[];
  onChange: (next: string[]) => void;
  placeholder?: string;
  maxValues?: number;
}

/**
 * Chip-style multi-string input.
 *
 *   - Type + Enter (or comma) appends the trimmed value as a tag.
 *   - Backspace on empty input removes the last tag.
 *   - Click "×" on a tag to remove it.
 *   - Duplicates and empty strings are ignored.
 */
export function TagInput({ id, values, onChange, placeholder, maxValues }: Props) {
  const fallbackId = useId();
  const inputId = id ?? fallbackId;
  const [draft, setDraft] = useState("");
  const atCap = maxValues !== undefined && values.length >= maxValues;

  const commit = (raw: string) => {
    const tag = raw.trim();
    if (!tag) return;
    if (values.includes(tag)) {
      setDraft("");
      return;
    }
    if (atCap) {
      setDraft("");
      return;
    }
    onChange([...values, tag]);
    setDraft("");
  };

  const removeAt = (idx: number) => {
    onChange(values.filter((_, i) => i !== idx));
  };

  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" || e.key === ",") {
      e.preventDefault();
      commit(draft);
    } else if (e.key === "Backspace" && draft === "" && values.length > 0) {
      e.preventDefault();
      removeAt(values.length - 1);
    }
  };

  return (
    <div className="tag-input">
      {values.map((tag, i) => (
        <span key={`${tag}-${i}`} className="tag-chip">
          <span>{tag}</span>
          <button
            type="button"
            className="tag-chip-remove"
            onClick={() => removeAt(i)}
            aria-label={`Remove ${tag}`}
          >
            ×
          </button>
        </span>
      ))}
      <input
        type="text"
        id={inputId}
        className="tag-input-field"
        value={draft}
        placeholder={
          atCap
            ? `Max ${maxValues} reached`
            : placeholder ?? "Type and press Enter…"
        }
        disabled={atCap}
        onChange={(event) => setDraft(event.target.value)}
        onKeyDown={onKeyDown}
        onBlur={() => commit(draft)}
      />
    </div>
  );
}
